// A small History API router: the site has a handful of fixed routes.

import { useSyncExternalStore } from "react";

export type Route =
  | { name: "trips"; tripId?: string; review: boolean }
  | { name: "mileage" }
  | { name: "export" }
  | { name: "report"; query: URLSearchParams }
  | { name: "account" }
  | { name: "signIn" }
  | { name: "devSession" };

function subscribe(onChange: () => void): () => void {
  window.addEventListener("popstate", onChange);
  window.addEventListener("carveny:navigate", onChange);
  return () => {
    window.removeEventListener("popstate", onChange);
    window.removeEventListener("carveny:navigate", onChange);
  };
}

function location(): string {
  return window.location.pathname + window.location.search;
}

export function parseRoute(path: string): Route {
  const url = new URL(path, "http://carveny.local");
  const parts = url.pathname.split("/").filter(Boolean);
  switch (parts[0]) {
    case "mileage":
      return { name: "mileage" };
    case "export":
      return { name: "export" };
    case "report":
      return { name: "report", query: url.searchParams };
    case "account":
      return { name: "account" };
    case "sign-in":
      return { name: "signIn" };
    case "dev-session":
      return { name: "devSession" };
    case "trips":
      return { name: "trips", tripId: parts[1], review: url.searchParams.get("review") === "1" };
    default:
      return { name: "trips", review: false };
  }
}

export function useRoute(): Route {
  const path = useSyncExternalStore(subscribe, location);
  return parseRoute(path);
}

export function navigate(path: string, options: { replace?: boolean } = {}): void {
  if (path === location()) return;
  if (options.replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  window.dispatchEvent(new Event("carveny:navigate"));
}

/** A link's click handler that navigates in place, leaving modified clicks to the browser. */
export function linkHandler(path: string) {
  return (event: React.MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(path);
  };
}
